import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
  Label,
  QualificationTrainingProviderType,
  QualificationTrainings,
  UpdateTrainingQualificationStatus,
  UpdateTrainingQualificationV2Dto,
} from '@wilson/interfaces';
import {
  TrainingQualificationsAttachmentGatewayService,
  TrainingQualificationsGateway,
} from '@wilson/qualification-v2/common';
import { cloneDeep, isEqual } from 'lodash';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import {
  BehaviorSubject,
  filter,
  forkJoin,
  Observable,
  of,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs';
import { TrainingQualificationsBulkEditAttachmentsSectionComponent } from './training-qualifications-bulk-edit-attachments-section/training-qualifications-bulk-edit-attachments-section.component';
import { TrainingQualificationsBulkEditDrawerService } from './training-qualifications-bulk-edit-drawer.service';
import { TrainingQualificationsBulkEditFooterSectionComponent } from './training-qualifications-bulk-edit-footer-section/training-qualifications-bulk-edit-footer-section.component';
import { TrainingQualificationsBulkEditGeneralSectionComponent } from './training-qualifications-bulk-edit-general-section/training-qualifications-bulk-edit-general-section.component';
import { TrainingQualificationsBulkEditInfoSectionComponent } from './training-qualifications-bulk-edit-info-section/training-qualifications-bulk-edit-info-section.component';
import { TrainingQualificationsBulkEditNotesSectionComponent } from './training-qualifications-bulk-edit-notes-section/training-qualifications-bulk-edit-notes-section.component';
import { TrainingQualificationsBulkEditSelectionHasMixedValues } from '../../../types';

@Component({
  selector: 'wilson-training-qualifications-bulk-edit-drawer',
  imports: [
    CommonModule,
    FormsModule,
    NzDrawerModule,
    NzFormModule,
    ReactiveFormsModule,
    TrainingQualificationsBulkEditAttachmentsSectionComponent,
    TrainingQualificationsBulkEditFooterSectionComponent,
    TrainingQualificationsBulkEditGeneralSectionComponent,
    TrainingQualificationsBulkEditInfoSectionComponent,
    TrainingQualificationsBulkEditNotesSectionComponent,
    TranslatePipe,
  ],
  templateUrl: './training-qualifications-bulk-edit-drawer.component.html',
  styleUrl: './training-qualifications-bulk-edit-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrainingQualificationsBulkEditDrawerComponent {
  private readonly trainingQualificationsBulkEditDrawerService = inject(
    TrainingQualificationsBulkEditDrawerService,
  );
  private readonly trainingQualificationsGateway = inject(
    TrainingQualificationsGateway,
  );
  private readonly trainingQualificationsAttachmentGateway = inject(
    TrainingQualificationsAttachmentGatewayService,
  );
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translateService = inject(TranslateService);

  private _savingDrawer: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  protected savingDrawer$: Observable<boolean> = this._savingDrawer
    .asObservable()
    .pipe(
      tap((saving) => {
        this.toggleFormControlsEnabledState(saving);
      }),
    );
  protected drawerVisible$: Observable<boolean> =
    this.trainingQualificationsBulkEditDrawerService.bulkEditDrawerVisible$;
  protected trainingsToEdit$: Observable<QualificationTrainings[]> =
    this.trainingQualificationsBulkEditDrawerService.trainingsToEdit$.pipe(
      filter((trainings) => trainings?.length > 0),
      tap((trainings) => {
        this.setFormControlValidators(trainings);
        this.setSelectionHasMixedValues(trainings);
        this.setContainsTrainingsFromDigitalProvider(trainings);
        this.setAttachmentsValues(trainings);
        this.setFormValuesBasedOnMixedStatus(trainings[0]);
        this.markFormAsPristine();
      }),
    );

  protected nameFormControl: FormControl<string | null> =
    new FormControl<string>('');
  protected labelFormControl: FormControl<string[] | null> = new FormControl<
    string[]
  >([]);
  protected statusFormControl: FormControl<UpdateTrainingQualificationStatus | null> =
    new FormControl<UpdateTrainingQualificationStatus | null>(null);
  protected notesFormControl: FormControl<string | null> =
    new FormControl<string>('', [Validators.maxLength(420)]);

  protected attachmentsList: NzUploadFile[] = [];
  private originalAttachmentsList: NzUploadFile[] = [];

  protected selectionHasMixedValues: TrainingQualificationsBulkEditSelectionHasMixedValues =
    {
      name: false,
      label: false,
      status: false,
      notes: false,
    };
  protected selectionContainsTrainingsFromDigitalProvider = false;

  protected closeBulkEditDrawer(): void {
    this.trainingQualificationsBulkEditDrawerService.setBulkEditDrawerVisible(
      false,
    );
  }

  protected cancelDrawer(): void {
    this.resetDrawer();
    this.closeBulkEditDrawer();
  }

  protected saveDrawer(): void {
    this._savingDrawer.next(true);
    this.trainingQualificationsBulkEditDrawerService.trainingsToEdit$
      .pipe(
        take(1),
        switchMap((trainings) => {
          const bulkDto: UpdateTrainingQualificationV2Dto[] = [];
          const requests: Observable<any>[] = [];
          trainings.forEach((training) => {
            const payload: UpdateTrainingQualificationV2Dto = {
              id: training.id,
            };
            if (this.nameFormControl.dirty && this.nameFormControl.value) {
              payload.name = this.nameFormControl.value;
            }
            if (this.labelFormControl.dirty && this.labelFormControl.value) {
              payload.labels = this.getSaveLabelsPayload(training);
            }
            if (this.statusFormControl.dirty && this.statusFormControl.value) {
              payload.status = this.statusFormControl.value;
            }
            if (this.notesFormControl.dirty) {
              payload.notes = this.getSaveNotesPayload(training);
            }
            if (Object.keys(payload).length > 1) {
              bulkDto.push(payload);
            }
          });

          if (bulkDto.length) {
            requests.push(
              this.trainingQualificationsGateway.bulkUpdateTrainingQualifications(
                bulkDto,
              ),
            );
          }

          if (this.haveAttachmentsChanged()) {
            const attachmentsPayload =
              this.prepareAttachmentsPayloads(trainings);
            if (attachmentsPayload.toAdd) {
              requests.push(
                this.trainingQualificationsAttachmentGateway.addAttachments(
                  attachmentsPayload.toAdd.addedAttachments,
                  attachmentsPayload.toAdd.addedAttachmentsUserQualificationIds,
                ),
              );
            }
            if (attachmentsPayload.toDelete) {
              requests.push(
                this.trainingQualificationsAttachmentGateway.deleteAttachments(
                  attachmentsPayload.toDelete.removedAttachmentsIds,
                ),
              );
            }
          }

          if (!requests.length) {
            return of(undefined).pipe(
              tap(() => {
                this.handleFailedDrawerSave();
              }),
            );
          } else {
            return forkJoin(requests).pipe(
              tap(() => {
                this.handleSuccessfulDrawerSave();
              }),
            );
          }
        }),
      )
      .subscribe();
  }

  private getSaveLabelsPayload(
    training: QualificationTrainings,
  ): Pick<Label, 'id'>[] {
    const labelsFormControlContainsMixed =
      this.labelFormControl.value?.includes('mixed') || false;
    const labelsFormControlValues =
      this.labelFormControl.value?.filter((label) => label !== 'mixed') || [];

    if (this.selectionHasMixedValues.label && labelsFormControlContainsMixed) {
      const existingLabelIds =
        training.labels?.map((label) => ({ id: label.id })) || [];
      const newLabelIds = labelsFormControlValues.map((label) => ({
        id: label,
      }));
      const combined = [...existingLabelIds, ...newLabelIds];

      return combined.filter(
        (potentialDuplicateLabel, index) =>
          combined.findIndex(
            (label) => label.id === potentialDuplicateLabel.id,
          ) === index,
      );
    } else {
      return labelsFormControlValues.map((label) => ({ id: label }));
    }
  }

  private getSaveNotesPayload(training: QualificationTrainings): string {
    let newNotes = this.notesFormControl.value || '';
    if (
      this.selectionHasMixedValues.notes &&
      this.selectionContainsTrainingsFromDigitalProvider &&
      training.notes
    ) {
      newNotes = training.notes + '\n\n' + this.notesFormControl.value;
    }
    return newNotes;
  }

  public haveAttachmentsChanged(): boolean {
    return (
      this.attachmentsList.some(
        (att) => !att.status || (att.status === 'removed' && att['id']),
      ) || this.attachmentsList.length < this.originalAttachmentsList.length
    );
  }

  private prepareAttachmentsPayloads(trainings: QualificationTrainings[]): {
    toAdd?: {
      addedAttachments: NzUploadFile[];
      addedAttachmentsUserQualificationIds: string[];
    };
    toDelete?: {
      removedAttachmentsIds: string[];
    };
  } {
    let attachmentsPayloads = {};
    const addedAttachments = this.attachmentsList.filter((att) => !att.status);
    const addedAttachmentsUserQualificationIds = trainings.map((s) => s.id);
    const removedAttachmentsStillInList = this.attachmentsList.filter(
      (att) => att.status === 'removed' && att['id'],
    );
    const removedAttachmentsOnlyInOriginal =
      this.originalAttachmentsList.filter(
        (oAtt) => !this.attachmentsList.some((att) => att['id'] === oAtt['id']),
      );
    const removedAttachmentsIds: string[] = removedAttachmentsStillInList
      .map((rAtt) => rAtt['id'])
      .concat(removedAttachmentsOnlyInOriginal.map((rAttO) => rAttO['id']));
    if (addedAttachments.length) {
      attachmentsPayloads = {
        toAdd: {
          addedAttachments,
          addedAttachmentsUserQualificationIds,
        },
      };
    }
    if (removedAttachmentsIds.length) {
      attachmentsPayloads = {
        ...attachmentsPayloads,
        toDelete: {
          removedAttachmentsIds,
        },
      };
    }
    return attachmentsPayloads;
  }

  private resetDrawer(): void {
    this.nameFormControl.reset();
    this.labelFormControl.reset();
    this.statusFormControl.reset();

    this.attachmentsList = [];
    this.notesFormControl.reset();

    this.trainingQualificationsBulkEditDrawerService.trainingsToEdit$
      .pipe(
        take(1),
        tap((trainingsToEdit) => {
          this.trainingQualificationsBulkEditDrawerService.setTrainingsToEdit(
            trainingsToEdit,
          );
        }),
      )
      .subscribe();
  }

  private setSelectionHasMixedValues(
    trainings: QualificationTrainings[],
  ): void {
    let hasMixedValuesResult: TrainingQualificationsBulkEditSelectionHasMixedValues =
      {
        name: false,
        label: false,
        status: false,
        notes: false,
      };

    if (trainings.length > 1) {
      const first = trainings[0];
      const firstName = first.name;
      const firstLabelIds = first.labels?.map((label) => label.id).sort() || [];
      const firstStatus = first.status;
      const firstNotes = first.notes;

      const nameMixed = trainings.some(
        (training) => training.name !== firstName,
      );
      const labelMixed = trainings.some((training) => {
        const currentLabelIds =
          training.labels?.map((label) => label.id).sort() || [];
        return !isEqual(currentLabelIds, firstLabelIds);
      });
      const statusMixed = trainings.some(
        (training) => training.status !== firstStatus,
      );
      const notesMixed = trainings.some(
        (training) => training.notes !== firstNotes,
      );

      hasMixedValuesResult = {
        name: nameMixed,
        label: labelMixed,
        status: statusMixed,
        notes: notesMixed,
      };
    }

    this.selectionHasMixedValues = hasMixedValuesResult;
  }

  private setContainsTrainingsFromDigitalProvider(
    trainings: QualificationTrainings[],
  ): void {
    this.selectionContainsTrainingsFromDigitalProvider = trainings.some(
      (training) =>
        training.organizationalUnitQualificationTrainingProvider
          .qualificationTrainingProvider.providerType ===
        QualificationTrainingProviderType.Digital,
    );
  }

  private setFormControlValidators(trainings: QualificationTrainings[]): void {
    if (trainings.length > 1) {
      this.nameFormControl.clearAsyncValidators();
      this.nameFormControl.clearValidators();
      this.statusFormControl.clearAsyncValidators();
      this.statusFormControl.clearValidators();
    } else {
      this.nameFormControl.addValidators([Validators.required]);
      this.statusFormControl.addValidators([Validators.required]);
    }

    this.nameFormControl.updateValueAndValidity();
    this.statusFormControl.updateValueAndValidity();
  }

  private setAttachmentsValues(trainings: QualificationTrainings[]): void {
    this.attachmentsList = [];
    if (
      trainings.length === 1 &&
      trainings[0].qualificationTrainingAttachments?.length
    ) {
      trainings[0].qualificationTrainingAttachments.forEach((attachment) => {
        const attachmentToNzUploadFile: NzUploadFile = {
          uid: attachment.id,
          id: attachment.id,
          name: attachment.fileName,
          status: 'done',
          url: attachment.s3Path,
          // I probably need to add the qualificationTrainingId here later
        };
        this.attachmentsList.push(attachmentToNzUploadFile);
      });
    }
    this.originalAttachmentsList = cloneDeep(this.attachmentsList);
  }

  private setFormValuesBasedOnMixedStatus(
    firstSelectionEntry: Pick<
      QualificationTrainings,
      'name' | 'labels' | 'status' | 'notes'
    >,
  ): void {
    this.nameFormControl.patchValue(
      this.selectionHasMixedValues.name ? null : firstSelectionEntry.name,
    );

    this.labelFormControl.patchValue(
      this.selectionHasMixedValues.label
        ? ['mixed']
        : firstSelectionEntry.labels?.map((label) => label.id).sort() || [],
    );
    this.statusFormControl.patchValue(
      this.selectionHasMixedValues.status
        ? null
        : (firstSelectionEntry.status as UpdateTrainingQualificationStatus),
    );
    this.notesFormControl.patchValue(
      this.selectionHasMixedValues.notes ? null : firstSelectionEntry.notes,
    );
  }

  private markFormAsPristine(): void {
    this.nameFormControl.markAsPristine();
    this.labelFormControl.markAsPristine();
    this.statusFormControl.markAsPristine();
    this.notesFormControl.markAsPristine();
  }

  private handleFailedDrawerSave(): Observable<undefined> {
    this.nzMessageService.error(this.translateService.instant('general.error'));

    timer(500)
      .pipe(take(1))
      .subscribe(() => {
        this._savingDrawer.next(false);
      });
    return of(undefined);
  }

  private handleSuccessfulDrawerSave(): void {
    this.nzMessageService.success(
      this.translateService.instant('general.saved_successfully'),
    );
    this.trainingQualificationsBulkEditDrawerService.triggerBulkEditedTrainings();
    this.resetDrawer();
    this.closeBulkEditDrawer();
    timer(100)
      .pipe(take(1))
      .subscribe(() => {
        this._savingDrawer.next(false);
      });
  }

  private toggleFormControlsEnabledState(saving: boolean): void {
    if (saving) {
      this.nameFormControl.disable();
      this.labelFormControl.disable();
      this.statusFormControl.disable();
      this.notesFormControl.disable();
    } else {
      this.nameFormControl.enable();
      this.labelFormControl.enable();
      this.statusFormControl.enable();
      this.notesFormControl.enable();
    }
  }
}
